import { Users, Link2, Lightbulb } from "lucide-react";

export const EmailBlacklistsTwoTypes = () => {
  const senderPoints = [
    "Track the sending IP address or the sending domain",
    "Built from observed sending behaviour and complaint patterns",
    "Examples include Spamhaus SBL and Spamhaus DBL",
    "Answer the question: should this sender be trusted",
  ];

  const contentPoints = [
    "Never look at who sent the message",
    "Scan the message body and list the domains found inside it",
    "Hyperlinks, tracking pixels and rewritten click tracking links",
    "Unsubscribe links, signature website links, plain text domain mentions",
    "Examples include SURBL and URIBL",
  ];

  return (
    <section className="py-10 sm:py-12">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3 text-center">
        Two fundamentally different types of list
      </h2>
      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
        Almost every argument about blacklists comes from mixing these two categories
        together. They measure different things and they are triggered by different causes.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Sender based lists
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Sender based lists record who sent the email. The identifier is the sending
            infrastructure itself, either the IP address or the domain used to send, and the
            listing follows from sending behaviour.
          </p>
          <ul className="space-y-2.5">
            {senderPoints.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Content based lists
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Content based lists ignore the sender completely. They index the domains that
            appear inside the message body, regardless of who put them there or which server
            delivered the mail.
          </p>
          <ul className="space-y-2.5">
            {contentPoints.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-warning mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 bg-primary/5 border border-primary/20 rounded-2xl p-5 sm:p-6">
        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-primary" />
          The practical consequence
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          A sending domain usually ends up exposed to content based lists through its own
          signature links and tracking links, because those domains sit inside the message
          body. That is why people conflate the two categories constantly, and why a content
          listing gets read as a sender reputation problem when it is not one. A plain text
          email with tracking switched off and no links keeps the sending domain invisible to
          content based lists entirely.
        </p>
      </div>
    </section>
  );
};